import {
  BottomBar,
  FormGroup,
  Header,
  Input,
  MainArea,
  Page,
  RadioButton,
  SaveBar,
  Select,
  SelectMulti,
} from '@salecycle/ui-component-library';
import { entityToTranslatedMultiSelectOptions } from 'components/utils/getSelectOptions';
import AppTopBar from 'core/ui/components/AppTopBar/AppTopBar';
import Spinner from 'core/ui/components/spinner';
import { IApiEntity } from 'entities/entity';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { SUPPORTED_LANGUAGES } from 'sections/client-management/ui/pages/client-settings/supported-languages/constants/SupportedLanguagesConstants';
import { getOnsiteTemplateTypeOptions, getMPSTemplateTypeOptions } from '../../constants';
import { TemplateManagementRoute } from '../../routes';
import { useGetChannels } from './utils/useGetChannels';
import { useGetTemplateTypes } from './utils/useGetTemplateTypes';
import { useSite } from 'sections/hooks/useSite';
import { SaveTemplatePayload } from '../../entities/templates';
import { useMutation } from 'react-query';
import * as RestApi from 'core/network/RestApi';
import { TemplateApiEndpoints } from '../../constants/api-endpoints';
import { RestApiError } from 'core/network/RestApi';
import useNotifications from 'core/notifications/useNotifications';
import { useFeatureFlags } from 'core/app/AppContext';
import qs from 'qs';
import { TemplateTypes } from 'sections/onsite-campaigns/types';
import { tabs } from '../template-list/constants/tabs';
import { useCallback } from 'react';

const templateTypesToTabMap = {
  [TemplateTypes.SendMyBasket]: tabs.SEND_MY_BASKET,
  [TemplateTypes.DataAcquisition]: tabs.DATA_ACQUISITION,
  [TemplateTypes.DisplayOnly]: tabs.DISPLAY_ONLY,
};

export interface OnsiteTemplateCreateParams {
  templateType?: TemplateTypes;
}

const useQueryParams = (): OnsiteTemplateCreateParams => {
  const removeLeadingQuestionMark = (str: string) => str.slice(1);

  const { search } = useLocation();
  return qs.parse(removeLeadingQuestionMark(search));
};

export const OnsiteTemplateCreate = () => {
  const { t } = useTranslation(['common', 'designs', 'supportedLanguages']);
  const queryParams = useQueryParams();
  const [templateName, setTemplateName] = React.useState<string>('');
  const [templateType, setTemplateType] = React.useState<string>(queryParams.templateType);
  const [languages, setLanguages] = React.useState<string[]>([]);
  const [isEmbedded, setIsEmbedded] = React.useState(false);
  const { push } = useHistory();
  const { siteId } = useParams<{ siteId: string }>();
  const { data: site } = useSite(siteId);
  const channelsQuery = useGetChannels();
  const templateTypesQuery = useGetTemplateTypes();
  const { showNotification } = useNotifications();
  const { pathname } = useLocation();
  const isMpsPage = pathname.includes('mps');
  const { flagEnabled } = useFeatureFlags();

  const toggleEmbedded = useCallback(() => {
    setIsEmbedded(!isEmbedded);
  }, [isEmbedded]);

  const handleReset = () => {
    setTemplateName('');
    setTemplateType('');
    setLanguages([]);
  };

  const handleExit = () => {
    push(generatePath(TemplateManagementRoute.HOME, { siteId, channel: 'onsite' }));
  };

  const isFormValid = () => {
    if (templateName.length > 0 && templateType !== '' && languages.length > 0) {
      return true;
    }
    return false;
  };

  const saveTemplate = useMutation((template: SaveTemplatePayload) =>
    RestApi.post<{ id: string }, SaveTemplatePayload>(
      generatePath(TemplateApiEndpoints.SAVE_ONSITE_TEMPLATE, { siteId }),
      template,
    ),
  );

  const handleSubmit = () => {
    const templateHtml = '<html>this is some html</html>';
    const newTemplate: SaveTemplatePayload = {
      type: templateType,
      channel: 'onsite',
      template: templateHtml,
      templateName,
      languages,
      isEmbedded,
      stylesheet: '{}',
    };

    saveTemplate.mutate(newTemplate, {
      onSuccess: data => {
        const channel = isMpsPage ? 'mps' : 'onsite';
        showNotification(t('designs:designGallery.form.comment.notifications.createTemplateSuccess'), 'normal');

        let path = generatePath(TemplateManagementRoute.HOME, { siteId, channel: channel });

        path +=
          '?' +
          qs.stringify({
            tab: templateTypesToTabMap[templateType as TemplateTypes],
            selectedTemplateId: data.data[0].id,
          });

        push(path);
      },
      onError: (error: RestApiError, variables, context) => {
        showNotification(t('designs:designGallery.form.errorHeading'), 'error');
      },
    });
  };

  const supportedLanguagesOptions = entityToTranslatedMultiSelectOptions<IApiEntity>(SUPPORTED_LANGUAGES, t);
  const getSiteLanguages = () => {
    return supportedLanguagesOptions.filter(option => {
      for (const supportedLanguage of site?.supportedLanguagesSettings?.supportedLanguages ?? []) {
        if (option.value === supportedLanguage) {
          return true;
        }
      }
      return false;
    });
  };

  if (channelsQuery.isLoading || templateTypesQuery.isLoading) {
    <Spinner />;
  }
  return (
    <>
      <MainArea>
        <AppTopBar />
        <Page>
          {isMpsPage ? (
            <Header heading={t('designs:designGallery.createMPSTemplate')} />
          ) : (
            <Header heading={t('designs:designGallery.createOnsiteTemplate')} />
          )}
          <Input
            label={t('designs:designGallery.form.templateName')}
            ariaLabel={t('designs:designGallery.form.templateName')}
            placeholder={t('designs:designGallery.form.templateNamePlaceholder')}
            value={templateName}
            onChange={e => setTemplateName(e)}
            validationMessage={
              templateName.length > 0 ? null : t('designs:designGallery.form.validation.minCharacters')
            }
            validationState={templateName.length > 0 ? 'complete' : 'warning'}
          />

          <FormGroup childrenSpanWidth={true}>
            <div data-testid={'templateTypeSelect'}>
              <Select
                ariaLabel={t('designs:designGallery.form.templateType.label')}
                label={t('designs:designGallery.form.templateType.label')}
                hint={t('designs:designGallery.form.templateType.labelHint')}
                options={isMpsPage ? getMPSTemplateTypeOptions(t) : getOnsiteTemplateTypeOptions(t, flagEnabled)}
                placeholder={t('designs:designGallery.form.templateType.templateTypePlaceholder')}
                value={
                  templateType
                    ? { value: templateType, label: t('designs:designGallery.form.templateType.' + templateType) }
                    : null
                }
                onChange={e => setTemplateType(e.value)}
                clearable={true}
                validationMessage={templateType === '' ? t('designs:designGallery.form.validation.required') : null}
                validationState={templateType === '' ? 'warning' : 'complete'}
              />
            </div>
            {flagEnabled('ld-embedded-content') ? (
              <FormGroup label={t('designs:designGallery.form.displayType')} variation="inline">
                <RadioButton
                  selected={!isEmbedded}
                  onClick={toggleEmbedded}
                  label={t('designs:designGallery.form.popUpLabel')}
                  aria-label={t('designs:designGallery.form.popUpLabelAria')}
                />
                <RadioButton
                  selected={isEmbedded}
                  onClick={toggleEmbedded}
                  label={t('designs:designGallery.form.embeddedLabel')}
                  aria-label={t('designs:designGallery.form.embeddedLabelAria')}
                />
              </FormGroup>
            ) : null}
            <div data-testid={'languagesSelect'}>
              <SelectMulti
                ariaLabel={t('designs:designGallery.form.languages.label')}
                label={t('designs:designGallery.form.languages.label')}
                hint={t('designs:designGallery.form.languages.labelHint')}
                options={getSiteLanguages()}
                placeholder={t('designs:designGallery.form.languages.languagesPlaceholder')}
                value={supportedLanguagesOptions.filter(sl => languages.includes(sl.value))}
                onChange={e =>
                  e && e.length && e.length > 0 ? setLanguages(e.map(option => option.value)) : setLanguages([])
                }
                clearable={true}
                validationMessage={
                  languages === undefined || languages.length === 0
                    ? t('designs:designGallery.form.validation.required')
                    : null
                }
                validationState={languages === undefined || languages.length === 0 ? 'warning' : 'complete'}
              />
            </div>
          </FormGroup>
        </Page>
        <BottomBar>
          <SaveBar
            enableSaveButton={isFormValid()}
            onSaveButtonClicked={handleSubmit}
            onDiscardButtonClicked={handleReset}
            hasValidationFailed={!isFormValid()}
            onExitButtonClicked={handleExit}
            exitButtonLabel={t('designs:designGallery.form.exit')}
          />
        </BottomBar>
      </MainArea>
    </>
  );
};

export default OnsiteTemplateCreate;
